import { useRouter } from 'next/router'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'

import { useGetCurrentUser } from '@/queries/user/useGetCurrentUser'
import useSignInMutation from '@/queries/user/useSignInMutation'
import useSignOutMutation from '@/queries/user/useSignOutMutation'
import { markSignOutNavigation, withSignOutInFlight } from '@/api/config'
import { isSafeReturnPath } from '@/lib/authReturnPath'
import { RETURN_PATH_PARAM } from '@/constants/authRedirect'
import { resolvePostSignInReturnPath } from '@/lib/desktopOAuthReturnPath'
import { clearReauthAttempts } from '@/lib/reauthCircuitBreaker'
import { clearSessionStorage } from '@/lib/sessionCleanup'
import { useUserStore } from '@/store/userStore' // [TEMPORARY] STORAGE_PRIMARY org용
import { SignInFormData } from '@/types/user.type'
import { toast } from '@/hooks/useToast'
import { ResponseCode } from '@/types/response.type'
import { USER_ROLES, SHARED_ROLES } from '@/constants/roles_and_permissions'

export function useAuth() {
  const router = useRouter()
  const queryClient = useQueryClient()
  const setOrganizationId = useUserStore((state) => state.setOrganizationId) // [TEMPORARY]

  const { mutateAsync: signInUserMutate } = useSignInMutation()
  const { mutateAsync: signOutUserMutate } = useSignOutMutation()
  const { data, isLoading, isError, refetch: refetchUser } = useGetCurrentUser()

  // 로그인 성공 후 돌아갈 곳.
  //
  // `from` 은 세션이 끊겼을 때 보고 있던 경로다. 이 값을 소비하는 곳은 **오직 여기(로그인
  // 성공 시점) 하나**여야 한다. 미들웨어가 함께 소비하면, 인터셉터가 sign_in으로 보내고
  // 미들웨어가 `from` 을 보고 앱으로 돌려보내는 새로운 무한 리다이렉트 고리가 생긴다.
  //
  // 비밀번호 변경 필요 등 세션이 있는 상태는 이동 후 미들웨어의 `getRedirectPath` 가 알아서
  // 교정하므로 여기서 중복 처리하지 않는다. Draft/Pending 은 애초에 로그인이 성공하지 않아
  // 이 함수에 도달하지 않는다 — `handleSignInError` 가 거부 코드로 안내 화면에 보낸다.
  const resolvePostSignInPath = (): string => {
    const from = router.query[RETURN_PATH_PARAM]
    return resolvePostSignInReturnPath(from, localStorage, isSafeReturnPath)
  }

  // SignIn
  const signInSession = async (data: SignInFormData) => {
    await signInUserMutate(data, {
      onSuccess: async () => {
        clearReauthAttempts()
        // router.push가 아니라 전체 로드여야 한다. 세션이 죽은 상태에서 클라이언트
        // 전환이 미들웨어 redirect를 맞으면 Next 라우터가 그 redirect를 데이터 캐시에
        // 저장하는데, `from`은 정확히 그 오염된 URL을 가리킨다. 같은 라우터 인스턴스로
        // push하면 네트워크 요청 없이 캐시 히트로 다시 sign_in에 튕긴다 (클러스터 E2E
        // 실측: push는 0ms 바운스, full load는 200). 전체 로드는 라우터를 새로 만든다.
        window.location.assign(resolvePostSignInPath())
      },
      onError: (e: any) => {
        handleSignInError(e)
      },
    })
  }

  // SignOut
  const signOutSession = async () => {
    try {
      // withSignOutInFlight: 성공/실패와 무관하게 플래그를 되돌린다. 예전에는 실패 시
      // 플래그가 켜진 채 남아 이후 모든 401 처리가 무력화됐다.
      await withSignOutInFlight(async () => {
        await signOutUserMutate()
        // 커넥터 토큰·투자 프로필 draft·legacy event map·analytics 대기열. 세션 만료 경로
        // (api/config.ts)와 같은 집합을 지운다.
        clearSessionStorage()
        setOrganizationId(null) // [TEMPORARY] 로그아웃 시 organization context 초기화
        // 로그아웃 시 모든 쿼리 데이터 삭제
        queryClient.clear()
        clearReauthAttempts()
        // 이동이 커밋되기 전까지 페이지는 살아 있다. 그 사이 남은 요청이 401을 받아도
        // "세션 만료" 리다이렉트로 이어지지 않도록 인터셉터를 닫는다.
        markSignOutNavigation()
        window.location.href = '/users/sign_in'
      })
    } catch (error: any) {
      toast({ type: 'error', text: 'Failed to sign out', duration: 2000 })
    }
  }

  const handleSignInError = (e: any) => {
    if (e.response?.data) {
      switch (e.response.data.code) {
        // Onboarding is unfinished, so the server issued no session — this code is the only
        // signal we get. Both destinations are public routes and need no user object.
        case ResponseCode.EMAIL_UNVERIFIED:
          router.push('/users/email/verify')
          break
        case ResponseCode.APPROVAL_PENDING:
          router.push('/users/pending')
          break
        case ResponseCode.INCORRECT_PASSWORD:
          toast({ type: 'error', text: 'The password is different.', duration: 2000 })
          break
        case ResponseCode.RECORD_NOT_EXISTS:
          toast({ type: 'error', text: 'The email does not exist.', duration: 2000 })
          break
        case ResponseCode.INVALID_PROVIDER:
          toast({
            type: 'error',
            text: e.response.data.message.split('AuthFailed - ')[1],
            duration: 2000,
          })
          break
        case ResponseCode.INVALID:
          toast({ type: 'error', text: e.response?.data.message, duration: 2000 })
          break
        default:
          toast({
            type: 'error',
            text: e.response.data.message || 'Failed to sign in',
            duration: 2000,
          })
      }
    }
  }

  const user = data?.data.response

  // [TEMPORARY] Sync organization_id to Zustand store for axios interceptor
  useEffect(() => {
    setOrganizationId(user?.organization?.id ?? null)
  }, [user?.organization?.id, setOrganizationId])

  const Role = useMemo(() => {
    const hasRole = (roleName: string) =>
      user?.roles?.some((role) => role.name === roleName) ?? false
    const hasOrgRole = (roleName: string) =>
      user?.organization?.roles?.some((role) => role.name === roleName) ?? false

    return {
      meeting_agent_excluded_user: hasRole(USER_ROLES.MeetingAgentExcludedUser),
      meeting_agent_org_vault: hasOrgRole(USER_ROLES.MeetingAgentOrgVault),
      workspace_model_show_default_name: hasOrgRole(USER_ROLES.WorkspaceModelShowDefaultName),
      meeting_agent_no_record_user: hasRole(USER_ROLES.MeetingAgentNoRecordUser),
      meeting_agent_hide_transcript: hasRole(USER_ROLES.MeetingAgentHideTranscript),
      company_page_tester: hasRole(USER_ROLES.CompanyPageTester),
      analytics_agent_tester: hasRole(USER_ROLES.AnalyticsAgentTester),
      workflow_admin: hasRole(SHARED_ROLES.WorkflowAdmin),
      catalyst_admin: hasRole(SHARED_ROLES.CatalystAdmin),
      meeting_agent_compliance_viewer: hasRole(USER_ROLES.MeetingAgentComplianceViewer),
      meeting_agent_compliance_tagger: hasRole(USER_ROLES.MeetingAgentComplianceTagger),
      meeting_agent_compliance_analyst: hasRole(USER_ROLES.MeetingAgentComplianceAnalyst),
      analytics_valut_excluded_user: hasRole(USER_ROLES.AnalyticsValutExcludedUser),
      model_selector_user: hasRole(USER_ROLES.ModelSelectorUser),
      watchlist_file_upload_disabled: hasRole(USER_ROLES.WatchlistFileUploadDisabled),
      recurring_meetings_manager: hasRole(USER_ROLES.RecurringMeetingsManager),
      // DEPRECATED — no client code reads this anymore (Credit Agent page removed),
      // kept only so the Role map doesn't silently drop a role name still assigned
      // server-side.
      credit_agent: hasRole(USER_ROLES.CreditAgent),
      // DEPRECATED (linq-alpha-server #3340) — no client code reads this anymore, kept
      // only so the Role map doesn't silently drop a role name still assigned server-side.
      enabled_hanwha_fund_dropdown: hasRole(USER_ROLES.EnabledHanwhaFundDropdown),
      enabled_ontology_selector: hasRole(USER_ROLES.EnabledOntologySelector),
      enabled_ontology_write: hasRole(USER_ROLES.EnabledOntologyWrite),
      // Gates the Hanwha CIO Daily Brief "문서(Documents)" tab under Briefings & Alerts.
      hanwha_market_briefing: hasRole(USER_ROLES.HanwhaMarketBriefing),
      enabled_artifact_page: hasRole(USER_ROLES.EnabledArtifactPage),
      // Unused since artifact chat opened to everyone (ESS-41).
      enabled_artifact_chat: hasRole(USER_ROLES.EnabledArtifactChat),
      // Gates the Company Deepdive LNB entry only — page access is unaffected.
      enabled_company_deepdive: hasRole(USER_ROLES.EnabledCompanyDeepdive),
      // Gates the Settings "Connectors" tab (LIN-721 pilot) — menu exposure only.
      enabled_microsoft_teams_connector: hasRole(USER_ROLES.EnabledMicrosoftTeamsConnector),
      // The Outlook connector's own role (LIN-1155) — gates the Outlook card and,
      // together with the Teams role, the Connectors tab.
      enabled_microsoft_outlook_connector: hasRole(USER_ROLES.EnabledMicrosoftOutlookConnector),
      // The Slack connector's own role (LIN-1055) — gates the Slack card and,
      // with the Microsoft roles, the Connectors tab.
      enabled_slack_connector: hasRole(USER_ROLES.EnabledSlackConnector),
      hanwha: hasRole(USER_ROLES.Hanwha),
      // 마스킹되지 않은 원본 파일 열람(Preview) 차단 대상 (PRD #3966).
      original_file_view_disabled: hasRole(USER_ROLES.OriginalFileViewDisabled),
      // 원본 파일 다운로드 차단 대상. view 와 독립 — 둘 다 막으려면 두 role 모두 부여.
      original_file_download_disabled: hasRole(USER_ROLES.OriginalFileDownloadDisabled),
      // Event Map pilot: granted per org (pilot orgs) or per user (internal smoke seats).
      event_map: hasOrgRole(USER_ROLES.EventMap) || hasRole(USER_ROLES.EventMap),
      data_vendor_snp_disabled:
        hasOrgRole(USER_ROLES.DataVendorSnpDisabled) || hasRole(USER_ROLES.DataVendorSnpDisabled),
      // Browser agent permissions: granted per organization or per user.
      cua_enabled: hasOrgRole(SHARED_ROLES.CuaEnabled) || hasRole(SHARED_ROLES.CuaEnabled),
      cua_sign_in: hasOrgRole(SHARED_ROLES.CuaSignIn) || hasRole(SHARED_ROLES.CuaSignIn),
      cua_totp_storage:
        hasOrgRole(SHARED_ROLES.CuaTotpStorage) || hasRole(SHARED_ROLES.CuaTotpStorage),
      cua_residential_egress:
        hasOrgRole(SHARED_ROLES.CuaResidentialEgress) || hasRole(SHARED_ROLES.CuaResidentialEgress),
    }
  }, [user?.roles, user?.organization?.roles])

  return {
    user,
    Role,
    signInSession,
    signOutSession,
    isLoading,
    isError,
    refetchUser,
  }
}
